import { Component, Host, Fragment, Element, h, Prop, Event, EventEmitter } from "@stencil/core";

import { StzhLinkFocusEvent, StzhLinkBlurEvent, StzhLinkSize, StzhBadgeType, StzhLinkClickEvent } from "../../index";

import { StzhLinkLocalizedText } from "./stzh-link.localization";
import { hasSlot, addWordBreakToLinks } from "../../utils/utils";

/**
 * @slot icon - Slot for icon element
 * @slot - Slot for label/text content
 */
@Component({
  tag: "stzh-link",
  styleUrl: "stzh-link.scss",
  scoped: true,
})
export class StzhLink {
  /** Translation strings */
  @Prop() localization: StzhLinkLocalizedText;

  /** Icon (use instead of icon slot) */
  @Prop() icon: string = "";

  /** Icon Position */
  @Prop({ reflect: true }) iconPosition: "left" | "right" = "left";

  /** Height variant (none prevent setting min-height for mikro and small breakpoint) */
  @Prop({ reflect: true }) height: "default" | "auto" = "default";

  /** Size variant */
  @Prop({ reflect: true }) size: StzhLinkSize = "default";

  /** Size variant (above small breakpoint) */
  @Prop({ reflect: true }) sizeSmall: StzhLinkSize;

  /** Size variant (above medium breakpoint) */
  @Prop({ reflect: true }) sizeMedium: StzhLinkSize;

  /** Size variant (above large breakpoint) */
  @Prop({ reflect: true }) sizeLarge: StzhLinkSize;

  /** Size variant (above ultra breakpoint) */
  @Prop({ reflect: true }) sizeUltra: StzhLinkSize;

  /** href attribute of the link */
  @Prop() href: string = "";

  /** Rel (if href is used) */
  @Prop() rel: string;

  /** target if the button is used as link (if href used)  */
  @Prop() target: string = "";

  /** Download attribute of link (if `href` used) */
  @Prop() download: string;

  /** type of the button, if used as button (if no href used) */
  @Prop() type: "button" | "submit" | "reset" = "button";

  /** Variant style */
  @Prop({ reflect: true }) variant: "default" | "secondary" | "back" = "default";

  /** Whether the link is full width */
  @Prop({ reflect: true }) fullwidth: boolean = false;

  /** Text alignment */
  @Prop({ reflect: true }) textAlign: "left" | "center" | "right" | "space-between" | "default" = "default";

  /** Badge text */
  @Prop() badge: string = "";

  /** Badge type */
  @Prop({ reflect: true }) badgeType: StzhBadgeType = "default";

  /** Whether badge should be displayed empty */
  @Prop({ reflect: true }) badgeEmpty: boolean = false;

  /** Badge position */
  @Prop({ reflect: true }) badgePosition: "icon" | "link" = "icon";

  /** Disabled status */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Active status */
  @Prop({ reflect: true }) active: boolean = false;

  /** Whether only an icon is used inside the link */
  @Prop({ reflect: true }) iconOnly: boolean = false;

  /** Label */
  @Prop() label: string = "";

  /** Access key of link (usually a number e.g. 1) */
  @Prop() linkAccesskey: string;

  /** ID of link element */
  @Prop() linkId: string;

  /** Accessible label for screen readers to replace visible text */
  @Prop({ attribute: "a11y-label" }) a11yLabel: string;

  /** Id for element which describes the button (this will be overwritten if description prop or slot is used, used by stzh-radiogroup) */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  /** Aria expanded of link/button */
  @Prop({ reflect: true, attribute: "a11y-expanded" }) a11yExpanded: boolean;

  /** Aria disabled of link/button */
  @Prop({ reflect: true, attribute: "a11y-disabled" }) a11yDisabled: boolean;

  /** Aria controls of link/button */
  @Prop({ attribute: "a11y-controls" }) a11yControls: string;

  /** Tabindex of link/button */
  @Prop({ attribute: "a11y-tabindex" }) a11yTabindex: string;

  /** Aria current of link/button */
  @Prop({ attribute: "a11y-current" }) a11yCurrent: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link/button element.
   * Default value will be taken from `label` prop or default slot.
   */
  @Prop() analyticsId: string;

  @Element() element: HTMLStzhLinkElement;

  /** Click event */
  @Event() stzhClick: EventEmitter<StzhLinkClickEvent>;

  /** Focus event */
  @Event() stzhFocus: EventEmitter<StzhLinkFocusEvent>;

  /** Input blur event */
  @Event() stzhBlur: EventEmitter<StzhLinkBlurEvent>;

  private link: HTMLButtonElement | HTMLAnchorElement;
  private text: HTMLDivElement;
  private focusedByInput: boolean = false;

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "link");
    }
  }

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.link.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-link",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-link",
      originalEvent: event,
    });
  };

  private onClick = (event: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-link",
      originalEvent: event,
      href: this.href,
    });
  };

  private addWordBreakToLinks() {
    const links = Array.from(this.element?.getElementsByClassName("stzh-link") || []);
    addWordBreakToLinks(links as HTMLElement[]);
  }

  private renderIcon(): HTMLInputElement {
    return (
      <div class="stzh-link__icon-wrapper">
        {this.icon ? <stzh-icon class="stzh-link__icon" name={this.icon}></stzh-icon> : <slot name="icon"></slot>}
        {(this.badge || this.badgeEmpty) && (
          <stzh-badge class="stzh-link__badge" label={this.badge} type={this.badgeType}></stzh-badge>
        )}
      </div>
    );
  }

  private renderContent(): HTMLDivElement {
    return (
      <div class="stzh-link__text">
        {this.rel && this.rel.includes("external") && (
          <div class="stzh-link__vhidden">{this.localization.$globals.externalLinkLabel}</div>
        )}
        {this.download && <div class="stzh-link__vhidden">{this.localization.$globals.downloadLinkLabel}</div>}
        <div ref={el => (this.text = el as HTMLDivElement)}>{this.label ? this.label : <slot></slot>}</div>
      </div>
    );
  }

  private renderInner(iconUsed: boolean): DocumentFragment {
    return (
      <Fragment>
        <div class="stzh-link__inner">
          {this.iconPosition === "left" && this.renderIcon()}
          {this.renderContent()}
          {this.iconPosition === "right" && this.renderIcon()}
        </div>
        {(this.badge || this.badgeEmpty) && !iconUsed && (
          <stzh-badge class="stzh-link__badge" label={this.badge} type={this.badgeType}></stzh-badge>
        )}
      </Fragment>
    );
  }

  async componentDidLoad() {
    this.addWordBreakToLinks();
  }

  componentDidRender() {
    requestAnimationFrame(() => {
      this.link?.setAttribute("s-object-id", this.analyticsId || this.text.innerText);
    });
  }

  render() {
    const iconUsed = hasSlot(this.element, "icon") || !!this.icon;
    const classes = {
      "stzh-link": true,
      "stzh-link--has-icon": iconUsed,
      "stzh-link--has-icon-only": this.iconOnly,
      "stzh-link--has-badge": !!this.badge,
      "stzh-link--is-fullwidth": this.fullwidth,
      "stzh-link--is-disabled": this.disabled,
      "stzh-link--is-active": this.active,
      [`stzh-link--badge-position-${this.badgePosition}`]: !!this.badgePosition,
      [`stzh-link--align-${this.textAlign}`]: !!this.textAlign,
      [`stzh-link--${this.variant}`]: !!this.variant,
    };

    return (
      <Host tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        {this.href ? (
          <a
            ref={el => (this.link = el as HTMLAnchorElement)}
            href={this.disabled ? null : this.href}
            download={this.download}
            rel={this.rel}
            target={this.target}
            class={classes}
            id={this.linkId}
            accessKey={this.linkAccesskey}
            tabindex={this.a11yTabindex}
            aria-label={this.a11yLabel || null}
            aria-describedby={this.a11yDescribedby || null}
            aria-disabled={typeof this.a11yDisabled !== "undefined" ? (this.a11yDisabled ? "true" : "false") : null}
            aria-expanded={typeof this.a11yExpanded !== "undefined" ? (this.a11yExpanded ? "true" : "false") : null}
            aria-controls={this.a11yControls || null}
            aria-current={this.a11yCurrent || null}
            onClick={this.onClick}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          >
            {this.renderInner(iconUsed)}
          </a>
        ) : (
          <button
            ref={el => (this.link = el as HTMLButtonElement)}
            class={classes}
            type={this.type}
            disabled={this.disabled}
            id={this.linkId}
            accessKey={this.linkAccesskey}
            tabindex={this.a11yTabindex}
            aria-label={this.a11yLabel || null}
            aria-describedby={this.a11yDescribedby || null}
            aria-disabled={typeof this.a11yDisabled !== "undefined" ? (this.a11yDisabled ? "true" : "false") : null}
            aria-expanded={typeof this.a11yExpanded !== "undefined" ? (this.a11yExpanded ? "true" : "false") : null}
            aria-controls={this.a11yControls || null}
            aria-current={this.a11yCurrent || null}
            onClick={this.onClick}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          >
            {this.renderInner(iconUsed)}
          </button>
        )}
      </Host>
    );
  }
}
